import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskDetailsProps } from 'models/tasks.types';
import socketStaff from 'socket/socketStaff';
import type { RootState } from 'store';
import { apiSlice } from 'store/api/apiSlice';

import { initialState, QueueTypes } from './queueSlice.types';

const queueApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => {
    return {
      getQueueScheduleDetails: build.query<QueueTypes, {}>({
        async onCacheEntryAdded(_, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
          try {
            const { data: cacheData } = await cacheDataLoaded;
            if (cacheData) {
              dispatch(setQueueScheduleDetails(cacheData));
            }

            const updateQueueData = () => {
              dispatch(apiSlice.util.invalidateTags(['QueueScheduleDetails']));
            };
            socketStaff.on('queueUpdate', updateQueueData);
          } catch {
            // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
            // in which case `cacheDataLoaded` will throw
          }
          await cacheEntryRemoved;
        },
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setQueueScheduleDetails(data));
          }
        },
        providesTags: ['QueueScheduleDetails'],
        query: () => ({
          url: `/appointment-queue/next`
        }),
        transformResponse: (response: { data: QueueTypes }) => response.data
      }),
      getQueueStatus: build.query<{ longestWaitTime: string | null }, void>({
        query: () => ({
          method: 'GET',
          url: `/appointment-queue/stats`
        }),
        transformResponse: (response: { data: { longestWaitTime: string | null } }) => response.data
      }),
      takeNextQueueTask: build.mutation<TaskDetailsProps, void>({
        invalidatesTags: ['QueueScheduleDetails'],
        query: () => ({
          method: 'POST',
          url: `/appointment-queue/take-next`
        }),
        transformResponse: (response: { data: TaskDetailsProps }) => response.data
      })
    };
  }
});

const queueSlice = createSlice({
  initialState,
  name: 'queue',
  reducers: {
    resetQueueScheduleDetails: (state) => {
      state.queueScheduleDetails = initialState.queueScheduleDetails;
    },
    resetQueueState: () => initialState,
    setQueueLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setQueueScheduleDetails: (state, action) => {
      state.queueScheduleDetails = action.payload;
    }
  }
});

export default queueSlice.reducer;

export const { resetQueueState, setQueueLoading, setQueueScheduleDetails } = queueSlice.actions;

export const selectQueue = (state: RootState) => state.queue;

export const {
  useLazyGetQueueScheduleDetailsQuery,
  useLazyGetQueueStatusQuery,
  useTakeNextQueueTaskMutation
} = queueApiSlice;
